import { useState, useEffect } from "react";
import { 
  X, Download, FileText, CheckCircle, AlertTriangle, TrendingUp,
  Layers, Users, Shield, GraduationCap, Globe, Search, ClipboardCheck,
  Cpu, BarChart3, Share2, Mail, Loader2
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CompareResults = ({ onClose }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [sessionData, setSessionData] = useState(null);
  const [selectedTools, setSelectedTools] = useState([]);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  const toolMeta = {
    certification_finder: { name: "Certification Finder", icon: Search, color: "bg-blue-500" },
    readiness_assessment: { name: "Readiness Assessment", icon: ClipboardCheck, color: "bg-green-500" },
    gap_identifier: { name: "Gap Identifier", icon: AlertTriangle, color: "bg-amber-500" },
    regulatory_checker: { name: "Regulatory Checker", icon: Shield, color: "bg-purple-500" },
    training_pathway: { name: "Training Pathway", icon: GraduationCap, color: "bg-indigo-500" },
    role_skills_identifier: { name: "Role Skills Identifier", icon: Users, color: "bg-pink-500" },
    digital_readiness_checker: { name: "Digital Readiness", icon: Cpu, color: "bg-cyan-500" },
    ims_analyzer: { name: "IMS Analyzer", icon: Layers, color: "bg-orange-500" }
  };

  useEffect(() => {
    loadSessionData();
  }, []);

  const loadSessionData = async () => {
    const sessionId = localStorage.getItem("chatbot_session_id");
    if (!sessionId) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/tools/session/${sessionId}`);
      setSessionData(response.data);
      // Auto-select all tools with results
      if (response.data?.tool_results) {
        setSelectedTools(Object.keys(response.data.tool_results));
      }
    } catch (error) {
      console.error("Failed to load session:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleTool = (toolId) => {
    setSelectedTools(prev => 
      prev.includes(toolId) 
        ? prev.filter(t => t !== toolId)
        : [...prev, toolId]
    );
  };

  const generateReport = () => {
    if (!sessionData?.tool_results) return null;

    const results = selectedTools.map(toolId => ({
      tool: toolMeta[toolId]?.name || toolId,
      data: sessionData.tool_results[toolId]
    }));

    // Extract key insights
    const insights = {
      certifications: [],
      gaps: [],
      regulations: [],
      training: [],
      risks: [],
      recommendations: []
    };

    results.forEach(({ tool, data }) => {
      if (!data) return;

      // Certification recommendations
      if (data.recommendations && tool.includes('Certification')) {
        data.recommendations.forEach(rec => {
          if (rec.standard) {
            insights.certifications.push({
              standard: rec.standard,
              priority: rec.priority,
              source: tool
            });
          }
        });
      }

      // Gaps
      if (data.gaps && Array.isArray(data.gaps)) {
        data.gaps.slice(0, 5).forEach(gap => {
          insights.gaps.push({
            description: gap.gap || gap.description || gap,
            severity: gap.severity || 'medium',
            source: tool
          });
        });
      }

      // Regulations
      if (data.regulations && Array.isArray(data.regulations)) {
        data.regulations.slice(0, 5).forEach(reg => {
          insights.regulations.push({
            name: reg.name,
            critical: reg.critical,
            source: tool
          });
        });
      }

      // Training
      if (data.training_plan || data.recommended_courses || data.pathway) {
        const courses = data.training_plan || data.recommended_courses || [];
        courses.slice(0, 5).forEach(course => {
          insights.training.push({
            name: course.course || course.name,
            priority: course.priority || 'medium',
            source: tool
          });
        });
      }

      // Risks
      if (data.risks && Array.isArray(data.risks)) {
        data.risks.forEach(risk => {
          insights.risks.push({
            area: risk.area,
            level: risk.level,
            description: risk.description,
            source: tool
          });
        });
      }

      // Recommendations
      if (data.recommendations && Array.isArray(data.recommendations)) {
        data.recommendations.slice(0, 3).forEach(rec => {
          if (rec.action || rec.category) {
            insights.recommendations.push({
              action: rec.action || rec.category,
              priority: rec.priority || 'medium',
              source: tool
            });
          }
        });
      }
      
      if (data.next_steps && Array.isArray(data.next_steps)) {
        data.next_steps.slice(0, 2).forEach(step => {
          insights.recommendations.push({
            action: step,
            priority: 'medium',
            source: tool
          });
        });
      }
    });

    return insights;
  };

  const downloadReport = () => {
    const insights = generateReport();
    if (!insights) return;

    const report = `
COMPLY CMS - COMPLIANCE ASSESSMENT REPORT
==========================================
Generated: ${new Date().toLocaleDateString()}
Session ID: ${sessionData?.session_id || 'N/A'}

TOOLS ANALYZED
--------------
${selectedTools.map(t => `• ${toolMeta[t]?.name || t}`).join('\n')}

${insights.certifications.length > 0 ? `
RECOMMENDED CERTIFICATIONS
--------------------------
${insights.certifications.map(c => `• ${c.standard} (${c.priority} priority) - from ${c.source}`).join('\n')}
` : ''}

${insights.gaps.length > 0 ? `
IDENTIFIED GAPS
---------------
${insights.gaps.map(g => `• [${g.severity?.toUpperCase()}] ${g.description} - from ${g.source}`).join('\n')}
` : ''}

${insights.regulations.length > 0 ? `
APPLICABLE REGULATIONS
----------------------
${insights.regulations.map(r => `• ${r.name}${r.critical ? ' (CRITICAL)' : ''} - from ${r.source}`).join('\n')}
` : ''}

${insights.training.length > 0 ? `
RECOMMENDED TRAINING
--------------------
${insights.training.map(t => `• ${t.name} (${t.priority} priority) - from ${t.source}`).join('\n')}
` : ''}

${insights.risks.length > 0 ? `
IDENTIFIED RISKS
----------------
${insights.risks.map(r => `• [${r.level?.toUpperCase()}] ${r.area}: ${r.description} - from ${r.source}`).join('\n')}
` : ''}

${insights.recommendations.length > 0 ? `
KEY RECOMMENDATIONS
-------------------
${insights.recommendations.map(r => `• ${r.action} - from ${r.source}`).join('\n')}
` : ''}

NEXT STEPS
----------
1. Review this report with your management team
2. Prioritize high-severity gaps and critical regulations
3. Develop an implementation roadmap with timelines
4. Contact CompLy CMS for expert consultation and support

==========================================
CompLy CMS | info@comply-cms.com | comply-cms.com
This report provides guidance only and is not an official certification determination.
    `.trim();

    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comply-cms-report-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleEmailResults = async () => {
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    const sessionId = localStorage.getItem("chatbot_session_id");
    if (!sessionId) {
      toast.error("No session found");
      return;
    }

    setEmailSending(true);
    try {
      await axios.post(`${API}/tools/email-results`, {
        email: email,
        session_id: sessionId,
        selected_tools: selectedTools
      });
      toast.success("Results sent to your email!");
      setShowEmailForm(false);
      setEmail("");
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Failed to send email";
      toast.error(errorMsg);
    } finally {
      setEmailSending(false);
    }
  };

  const insights = generateReport();
  const hasResults = sessionData?.tool_results && Object.keys(sessionData.tool_results).length > 0;

  if (isLoading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading your results...</p>
      </div>
    );
  }

  if (!hasResults) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-primary">Compare Results</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>
        <div className="text-center py-12">
          <BarChart3 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Results Yet</h3>
          <p className="text-muted-foreground mb-6">
            Complete some diagnostic tools to compare and combine your results.
          </p>
          <Button onClick={onClose} className="bg-brand hover:bg-brand-dark">
            Start with a Tool
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-primary">Compare & Export Results</h2>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Tool Selection */}
      <div className="mb-6">
        <h3 className="font-semibold mb-3">Select tools to include:</h3>
        <div className="flex flex-wrap gap-2">
          {Object.keys(sessionData.tool_results).map(toolId => {
            const meta = toolMeta[toolId];
            const Icon = meta?.icon || FileText;
            return (
              <button
                key={toolId}
                onClick={() => toggleTool(toolId)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
                  selectedTools.includes(toolId)
                    ? 'border-brand bg-brand/5'
                    : 'border-slate-200 hover:border-brand/50'
                }`}
              >
                <div className={`w-6 h-6 rounded ${meta?.color || 'bg-slate-500'} flex items-center justify-center`}>
                  <Icon className="h-3 w-3 text-white" />
                </div>
                <span className="text-sm font-medium">{meta?.name || toolId}</span>
                {selectedTools.includes(toolId) && (
                  <CheckCircle className="h-4 w-4 text-brand" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {selectedTools.length > 0 && insights && (
        <>
          {/* Combined Insights */}
          <div className="space-y-4 mb-6">
            {/* Certifications */}
            {insights.certifications.length > 0 && (
              <div className="bg-blue-50 rounded-xl p-4">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Search className="h-4 w-4 text-blue-600" />
                  Recommended Certifications
                </h4>
                <div className="flex flex-wrap gap-2">
                  {insights.certifications.map((cert, i) => (
                    <Badge key={i} className={
                      cert.priority === 'high' ? 'bg-red-100 text-red-700' :
                      cert.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                      'bg-green-100 text-green-700'
                    }>
                      {cert.standard}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Gaps */}
            {insights.gaps.length > 0 && (
              <div className="bg-amber-50 rounded-xl p-4">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  Identified Gaps ({insights.gaps.length})
                </h4>
                <div className="space-y-1">
                  {insights.gaps.slice(0, 5).map((gap, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <Badge variant="outline" className={
                        gap.severity === 'high' ? 'border-red-300 text-red-700' :
                        'border-amber-300 text-amber-700'
                      }>
                        {gap.severity}
                      </Badge>
                      <span>{gap.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Risks */}
            {insights.risks.length > 0 && (
              <div className="bg-red-50 rounded-xl p-4">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Shield className="h-4 w-4 text-red-600" />
                  Identified Risks ({insights.risks.length})
                </h4>
                <div className="space-y-2">
                  {insights.risks.map((risk, i) => (
                    <div key={i} className="bg-white rounded-lg p-2">
                      <div className="flex items-center gap-2">
                        <Badge className={risk.level === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                          {risk.level}
                        </Badge>
                        <span className="font-medium text-sm">{risk.area}</span>
                      </div>
                      <p className="text-xs text-slate-600 mt-1">{risk.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Training */}
            {insights.training.length > 0 && (
              <div className="bg-indigo-50 rounded-xl p-4">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <GraduationCap className="h-4 w-4 text-indigo-600" />
                  Recommended Training
                </h4>
                <div className="flex flex-wrap gap-2">
                  {insights.training.slice(0, 6).map((t, i) => (
                    <Badge key={i} variant="outline" className="border-indigo-300">
                      {t.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Key Recommendations */}
            {insights.recommendations.length > 0 && (
              <div className="bg-green-50 rounded-xl p-4">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-green-600" />
                  Key Recommendations
                </h4>
                <ul className="space-y-1">
                  {insights.recommendations.slice(0, 5).map((rec, i) => (
                    <li key={i} className="text-sm flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                      {rec.action}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Email Form */}
          {showEmailForm && (
            <div className="bg-blue-50 rounded-xl p-4 mb-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Mail className="h-4 w-4 text-blue-600" />
                Email Your Results
              </h4>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className="flex-1"
                  data-testid="email-results-input"
                />
                <Button 
                  onClick={handleEmailResults}
                  disabled={emailSending || !email.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="send-email-btn"
                >
                  {emailSending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Send"
                  )}
                </Button>
                <Button 
                  variant="ghost"
                  onClick={() => setShowEmailForm(false)}
                >
                  Cancel
                </Button>
              </div>
              <p className="text-xs text-blue-700 mt-2">
                We&apos;ll send a summary of your selected tool results to this email.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={downloadReport}
              className="flex-1"
              data-testid="download-report-btn"
            >
              <Download className="mr-2 h-4 w-4" />
              Download
            </Button>
            <Button 
              variant="outline"
              onClick={() => setShowEmailForm(true)}
              className="flex-1"
              data-testid="email-results-btn"
            >
              <Mail className="mr-2 h-4 w-4" />
              Email Results
            </Button>
            <Button 
              onClick={onClose}
              className="flex-1 bg-brand hover:bg-brand-dark"
            >
              Get Expert Help
            </Button>
          </div>
        </>
      )}

      {selectedTools.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          Select at least one tool to view combined results.
        </div>
      )}
    </div>
  );
};

export default CompareResults;
